'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Skeleton } from '@common/components';
import SvgCheck from '@common/icon/SvgCheck';
import SvgChevronright from '@common/icon/SvgChevronright';
import { cn } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense, useEffect, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { AuthQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { DONATION_ROUTES, STORAGE_KEY } from '@/constants';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { tryOpenResponsiveDialog } from '@/stores/ResponsiveDialogStore';
import { resolvePartnerWebviewGate } from '@/utils/resolvePartnerWebviewGate';
import { resolveMemberStatus } from '../_utils/resolveMemberStatus';
import { retryUnlessReturnCode } from '../_utils/retryUnlessReturnCode';
import { createReviewPendingDialog } from './ReviewPendingSheet';
export const MemberStatus = t0 => {
  const $ = _c(1);
  const {
    isServerLogin
  } = t0;
  const isLogin = useAuthStore(_temp) ?? isServerLogin;
  if (!isLogin) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ErrorBoundary fallback={null}><Suspense fallback={<Skeleton className="h-68 w-full rounded-medium" />}><MemberStatusContent /></Suspense></ErrorBoundary>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  return t1;
};
const MESSAGE_BY_STATUS = {
  APPLIED_WAITING: '신청완료 (선정 대기중)',
  REVIEW_PENDING: '답례품을 받은 날부터 10일 안에 후기를 남겨주세요',
  REVIEW_RESTRICTED: '후기 미작성으로 체험단 신청이 불가능해요.',
  UNAVAILABLE: '체험단 신청이 불가능해요.'
};
const hasShownReviewSheet = (applicationId: string) => {
  try {
    return localStorage.getItem(STORAGE_KEY.GIFT_TRIAL_REVIEW_SHEET_SHOWN_APPLICATION_ID) === applicationId;
  } catch {
    //* 저장소가 막힌 웹뷰에선 기록을 못 남겨 매 진입마다 뜨므로 띄우지 않는다 — 카드에 같은 안내가 있다
    return true;
  }
};
const MemberStatusContent = () => {
  const $ = _c(34);
  const hasOpenedReviewDialogRef = useRef(false);
  const canAutoOpenReviewDialog = useDeviceInfoStore(_temp2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      ...AuthQueryOptions["get:/wello/v2/auth/gift-trial/me"](),
      retry: retryUnlessReturnCode,
      select: _temp3
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: status
  } = useSuspenseQuery(t0);
  const nickName = useAuthStore(_temp4);
  const reviewApplicationId = status?.type === "REVIEW_PENDING" ? status.reviewApplicationId : undefined;
  let t1;
  let t2;
  if ($[1] !== canAutoOpenReviewDialog || $[2] !== reviewApplicationId) {
    t1 = () => {
      if (!reviewApplicationId || !canAutoOpenReviewDialog || hasOpenedReviewDialogRef.current || hasShownReviewSheet(reviewApplicationId)) {
        return;
      }
      hasOpenedReviewDialogRef.current = tryOpenResponsiveDialog(createReviewPendingDialog(reviewApplicationId));
      if (hasOpenedReviewDialogRef.current) {
        try {
          localStorage.setItem(STORAGE_KEY.GIFT_TRIAL_REVIEW_SHEET_SHOWN_APPLICATION_ID, reviewApplicationId);
        } catch {}
      }
    };
    t2 = [reviewApplicationId, canAutoOpenReviewDialog];
    $[1] = canAutoOpenReviewDialog;
    $[2] = reviewApplicationId;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  if (!status) {
    return null;
  }
  const memberName = nickName ? `${nickName}님` : "\uD68C\uC6D0\uB2D8";
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <CustomLink href={DONATION_ROUTES.GIFT_TRIAL_MY.pathname}>내 체험단 활동<SvgChevronright className="size-12 text-Gray-600" /></CustomLink>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const myActivityLink = t3;
  if (status.type === "ELIGIBLE") {
    let t4;
    if ($[6] !== memberName) {
      t4 = <strong className="font-700 text-Gray-900 typo-body3">{memberName}</strong>;
      $[6] = memberName;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    let t5;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <p className="flex items-center gap-4 font-500 text-Gray-900 typo-body4">체험단 신청이 가능해요<span className="size-17 shrink-0 rounded-pill bg-B-500 flex-center"><SvgCheck className="size-12 text-Gray-white" /></span></p>;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] !== t4) {
      t6 = <div className="flex min-w-0 flex-col gap-4">{t4}{t5}</div>;
      $[9] = t4;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    let t7;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Button theme="sub-line" dimensions="H32" className="shrink-0" asChild={true}>{myActivityLink}</Button>;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    let t8;
    if ($[12] !== t6) {
      t8 = <MemberCard className="flex-row items-center justify-between gap-8 pr-12">{t6}{t7}</MemberCard>;
      $[12] = t6;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    return t8;
  }
  if (status.type === "NO_DONATION") {
    let t4;
    if ($[14] !== memberName) {
      t4 = <strong className="font-700 text-Gray-900 typo-body3">{memberName}</strong>;
      $[14] = memberName;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    let t5;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <p className="font-500 text-Gray-700 typo-body4">체험단에 신청하려면 기부 이력이 필요해요.</p>;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    let t6;
    if ($[17] !== t4) {
      t6 = <div className="flex flex-col gap-4">{t4}{t5}</div>;
      $[17] = t4;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    let t7;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Button dimensions="H32" className="w-full" asChild={true}><CustomLink href={DONATION_ROUTES.GIFTS.pathname}>기부하러 가기<SvgChevronright className="size-12" /></CustomLink></Button>;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    let t8;
    if ($[20] !== t6) {
      t8 = <MemberCard className="gap-16">{t6}{t7}</MemberCard>;
      $[20] = t6;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    return t8;
  }
  let t4;
  if ($[22] !== memberName) {
    t4 = <strong className="font-700 text-Gray-900 typo-body3">{memberName}</strong>;
    $[22] = memberName;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  const t5 = MESSAGE_BY_STATUS[status.type];
  let t6;
  if ($[24] !== t5) {
    t6 = <p className="font-500 text-Gray-700 typo-body4">{t5}</p>;
    $[24] = t5;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== t4 || $[27] !== t6) {
    t7 = <div className="flex min-w-0 flex-col gap-4">{t4}{t6}</div>;
    $[26] = t4;
    $[27] = t6;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== status.type) {
    t8 = <Button theme="sub-line" dimensions="H32" className="w-full" asChild={true}>{status.type === "REVIEW_PENDING" ? <CustomLink href={DONATION_ROUTES.GIFT_TRIAL_MY.withSearchParams({
        searchParams: {
          filter: "SELECTED"
        }
      })}>후기 작성하러 가기<SvgChevronright className="size-12" /></CustomLink> : myActivityLink}</Button>;
    $[29] = status.type;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== t7 || $[32] !== t8) {
    t9 = <MemberCard className="gap-16">{t7}{t8}</MemberCard>;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  return t9;
};
const MemberCard = t0 => {
  const $ = _c(5);
  const {
    className,
    children
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex flex-col rounded-medium border border-Gray-550 bg-Gray-white px-16 py-12", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
function _temp(state) {
  return state.isLogin;
}
function _temp2(state) {
  return resolvePartnerWebviewGate(state) === "ALLOWED";
}
function _temp3(t0) {
  const {
    context
  } = t0;
  return context ? resolveMemberStatus(context) : null;
}
function _temp4(state_0) {
  return state_0.myInfo?.nickName;
}